import { History } from "history";
import React from "react";

import { HistoryContext } from "./HistoryContext";

/**
 * Specification of props required to render links.
 */
export type LinkProps = {
  /**
   * If specified, this class will be applied the the rendered a-tag, when the
   * link is currently active.
   *
   * If nothing else is provided, this will default to "active".
   */
  activeClassName?: string;

  /**
   * If specified, this class will be applied to the rendered a-tag, so that
   * you can properly target styling of the element.
   */
  className?: string;

  /**
   * When set to true, the link will be opened in a new window rather than
   * internally on the same page.
   */
  external?: boolean;

  /**
   * Makes it possible to propagate an onClick-listener down to the actual
   * rendered a-tag.
   */
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;

  /**
   * Contains a reference to the History instance that's used by the router
   * that this link was wrapped inside.
   */
  history?: History;

  /**
   * Allows us to inject an initial state into the link, which is useful when
   * rendering through server side.
   */
  initialState?: LinkState;

  /**
   * Specifies if click on the link should replace the current page in the
   * history (as opposed to pushing a new history entry).
   */
  replace?: boolean;

  /**
   * Specifies the path that the user should be taken to, when he clicks the
   * link.
   */
  to: string;

  children: React.ReactNode
};

/**
 * Specification of lifecycle state of the Link.
 */
export type LinkState = {
  /**
   * Keeps track of whether or not this link is the currently active one (ie.
   * if the "to"-value matches the currently opened path).
   * Specifies the link is currently active
   */
  isActive: boolean;
};

/**
 * The links navigates to the given path on click, using the routers built-in
 * navigation system.
 */
export class LinkUnwrapped extends React.PureComponent<LinkProps> {
  /**
   * Setup initial state, so we know from the beginning if the link happens to
   * be active.
   */
  public state: LinkState = this.props.initialState || {
    isActive: this.isActive(),
  };

  /**
   * Will contain a reference to the unregister callback, which we can use to
   * stop listening for history events.
   */
  private unregisterHistoryListener?: () => void;

  /**
   * A state indicating if this component is currently mounted in the DOM, so
   * we can determine if it's safe to use .setState().
   */
  private isComponentMounted: boolean = false;

  /**
   * Set up an event listener, that'll make sure to handle user navigation and
   * update state when the link is activated/deactivated.
   */
  public componentDidMount(): void {
    // Throw an error if attempting to mount with undefined history
    // reference
    if (!this.props.history) {
      throw new Error(
        "<Link />: Mounting a link outside of a <Router /> context isn't allowed."
      );
    }

    this.unregisterHistoryListener = this.props.history.listen(
      this.onNavigation
    );

    // Register that the component is currently mounted, so  we know that
    // we can safely update state
    this.isComponentMounted = true;
  }

  /**
   * Unbind event listeners when the component is unmounted
   */
  public componentWillUnmount(): void {
    // Stop listening for navigation events
    if (this.unregisterHistoryListener) {
      this.unregisterHistoryListener();
    }

    // Register that the component is currently unmounted, so  we know that
    // we can  no longer update state
    this.isComponentMounted = false;
  }

  /**
   * Inject the link into the dom using a native <a ... /> tag, but overwrite
   * it's default behaviour by assigning a click listener to  it.
   */
  public render(): React.JSX.Element {
    const { activeClassName, children, className, to } = this.props;
    const { isActive } = this.state;

    // Generate class name to apply to the link based on current state
    const combinedClassName = [
      className ? className : "",
      isActive ? activeClassName || "active" : "",
    ].join(" ");

    return (
      <a className={combinedClassName} href={to} onClick={this.onClick}>
        {children}
      </a>
    );
  }

  /**
   * Listener that'll be triggered when the user clicks the link (at this
   * point we should navigate to the desired page using the history manager
   * from the router).
   */
  private readonly onClick = (evt: React.MouseEvent<HTMLAnchorElement>) => {
    // Throw an error if attempting to mount with undefined history
    // reference
    if (!this.props.history) {
      throw new Error(
        "<Link />.onClick: Lost reference to <Router /> context."
      );
    }

    const isExternal = this.props.external === true;
    const isInternal = !isExternal && this.props.to.indexOf("//") === -1;

    // If an onClick handler was given, then trigger it
    if (this.props.onClick) {
      this.props.onClick(evt);
    }

    // Handle internal links within our router context
    if (isInternal) {
      if (this.props.replace !== true) {
        this.props.history.push(this.props.to);
      } else {
        this.props.history.replace(this.props.to);
      }

      // Open external links in new windows
    } else if (isExternal) {
      // This WILL break if being run in anothing other than a browser
      // environment... For now we accept this, as it's only going to
      // happen inside a user instantiated event and we only support
      // react-dom rendering at the moment :-)
      //
      // In the future we might want to investigate ways to properly
      // handle external events within a react-native environment, so
      // that this component is truly universal
      window.open(this.props.to, "_blank");
    }

    // Abort default handling of click on the link if we just managed it
    if (isInternal || isExternal) {
      evt.preventDefault();
    }
  };

  /**
   * Listener that's triggered whenever the user navigates around the site,
   * automatically detecting if the link is the currently active one
   */
  private readonly onNavigation = () => {
    setTimeout(() => {
      // Bail out if component isn't mounted any longer...
      if (!this.isComponentMounted) {
        return;
      }

      const isActive = this.isActive();

      if (this.state.isActive !== isActive) {
        this.setState({ isActive });
      }
    });
  };

  /**
   * Helper that'll determine if the link is currently active (ie. if it's
   * pathname is exactly equal to the current pathname in the history).
   */
  private isActive(): boolean {
    return this.props.history
      ? this.props.to.split("?")[0].split("#")[0] ===
          this.props.history.location.pathname
      : false;
  }
}

/**
 * Wrapper around the Link, which makes sure that the reference to our history
 * object is available as a prop.
 */
// tslint:disable-next-line variable-name
export const Link: React.FC<LinkProps> = (props: LinkProps) => (
  <HistoryContext.Consumer>
    {(history) => <LinkUnwrapped {...props} history={history} />}
  </HistoryContext.Consumer>
);
